import React, { useState, useEffect } from 'react';
import {
  markAttendance,
  getAttendanceByDate,
  getAttendanceReport,
  updateAttendance,
  deleteAttendance,
  getEmployeeAttendance,
  getEmployeeAttendanceSummary
} from '../../services/attendanceApi';
import { useAuth } from '../../contexts/AuthContext';
import apiWithAuth from '../../services/apiWithAuth';

const AttendanceManagement = () => {
  const { canAccessModule } = useAuth();
  const [activeView, setActiveView] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Dashboard State
  const [dashboardData, setDashboardData] = useState({
    monthlyStats: [],
    summary: { present: 0, absent: 0, leave: 0, total: 0 },
    attendanceDetails: []
  });

  // Mark Attendance State
  const [markForm, setMarkForm] = useState({
    employeeId: '',
    date: new Date().toISOString().split('T')[0],
    status: 'present',
    notes: ''
  });

  // Employee List State
  const [employees, setEmployees] = useState([]);

  // View Attendance State
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceList, setAttendanceList] = useState([]);

  // Report State
  const [reportFilters, setReportFilters] = useState({
    startDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    department: ''
  });
  const [reportData, setReportData] = useState([]);

  // Employee Summary State
  const [employeeSummary, setEmployeeSummary] = useState({
    selectedEmployeeId: '',
    startDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    data: null
  });

  // Edit Modal State
  const [editModal, setEditModal] = useState({
    isOpen: false,
    attendance: null
  });

  const statusColors = {
    present: 'bg-green-100 text-green-800',
    absent: 'bg-red-100 text-red-800',
    'half-day': 'bg-yellow-100 text-yellow-800',
    leave: 'bg-blue-100 text-blue-800'
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (activeView === 'dashboard') {
      fetchDashboardData();
    } else if (activeView === 'view') {
      fetchAttendanceByDate();
    } else if (activeView === 'report') {
      fetchReport();
    } else if (activeView === 'employee') {
      if (employeeSummary.selectedEmployeeId) {
        fetchEmployeeSummary();
      }
    }
  }, [activeView, selectedDate, selectedMonth, selectedYear, employeeSummary.selectedEmployeeId]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Fetch Employees
  const fetchEmployees = async () => {
    try {
      const response = await apiWithAuth.get('/employees');
      if (response.data.success) {
        setEmployees(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  // Fetch Dashboard Data (Month-wise)
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Get first and last day of selected month
      const startDate = new Date(selectedYear, selectedMonth - 1, 1).toISOString().split('T')[0];
      const endDate = new Date(selectedYear, selectedMonth, 0).toISOString().split('T')[0];
      
      const response = await getAttendanceReport(startDate, endDate);
      
      if (response.data.success && response.data.data.length > 0) {
        const allEmployeesData = response.data.data;
        
        // Calculate daily stats for the month
        const dailyStats = calculateDailyStatsFromData(allEmployeesData, selectedYear, selectedMonth);
        
        // Calculate total summary from all employees
        let totalSummary = { present: 0, absent: 0, leave: 0, 'half-day': 0 };
        allEmployeesData.forEach(emp => {
          const summary = emp.summary || {};
          totalSummary.present += summary.present || 0;
          totalSummary.absent += summary.absent || 0;
          totalSummary.leave += summary.leave || 0;
          totalSummary['half-day'] += summary['half-day'] || 0;
        });
        
        const total = totalSummary.present + totalSummary.absent + totalSummary.leave + totalSummary['half-day'];
        
        setDashboardData({
          monthlyStats: dailyStats,
          summary: {
            present: totalSummary.present,
            absent: totalSummary.absent,
            leave: totalSummary.leave,
            halfDay: totalSummary['half-day'],
            total
          },
          attendanceDetails: allEmployeesData
        });
      } else {
        // No data available
        setDashboardData({
          monthlyStats: [],
          summary: { present: 0, absent: 0, leave: 0, halfDay: 0, total: 0 },
          attendanceDetails: []
        });
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showMessage('error', 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateDailyStatsFromData = (allEmployeesData, year, month) => {
    // Get number of days in the selected month
    const daysInMonth = new Date(year, month, 0).getDate();
    const dailyData = {};
    
    // Initialize daily data for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      dailyData[day] = { present: 0, absent: 0, leave: 0, halfDay: 0 };
    }
    
    // Aggregate data from all employees
    allEmployeesData.forEach(empData => {
      // Check if employee has records array
      if (empData.records && Array.isArray(empData.records) && empData.records.length > 0) {
        empData.records.forEach(record => {
          const recordDate = new Date(record.date);
          const recordYear = recordDate.getFullYear();
          const recordMonth = recordDate.getMonth() + 1;
          const day = recordDate.getDate();
          
          // Only count records from the selected month and year
          if (recordYear === year && recordMonth === month && dailyData[day]) {
            if (record.status === 'present') dailyData[day].present++;
            else if (record.status === 'absent') dailyData[day].absent++;
            else if (record.status === 'leave') dailyData[day].leave++;
            else if (record.status === 'half-day') dailyData[day].halfDay++;
          }
        });
      }
    });
    
    // Convert to array format for chart
    return Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      return {
        day,
        present: dailyData[day].present,
        absent: dailyData[day].absent,
        leave: dailyData[day].leave,
        halfDay: dailyData[day].halfDay
      };
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      'P': 'Present',
      'A': 'Absent',
      'L': 'Leave',
      'HD': 'Half Day',
      'LC': 'Leave Credit',
      'S': 'Sunday/Holiday'
    };
    return labels[status] || status;
  };

  // Mark Attendance
  const handleMarkAttendance = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await markAttendance(markForm);
      if (response.data.success) {
        showMessage('success', 'Attendance marked successfully');
        setMarkForm({
          employeeId: '',
          date: new Date().toISOString().split('T')[0],
          status: 'present',
          notes: ''
        });
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to mark attendance');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Attendance by Date
  const fetchAttendanceByDate = async () => {
    setLoading(true);
    try {
      const response = await getAttendanceByDate(selectedDate);
      if (response.data.success) {
        setAttendanceList(response.data.data);
      }
    } catch (error) {
      showMessage('error', 'Failed to fetch attendance records');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Report
  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await getAttendanceReport(
        reportFilters.startDate,
        reportFilters.endDate,
        reportFilters.department || null
      );
      if (response.data.success) {
        setReportData(response.data.data);
      }
    } catch (error) {
      showMessage('error', 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Employee Summary
  const fetchEmployeeSummary = async () => {
    if (!employeeSummary.selectedEmployeeId) {
      showMessage('error', 'Please select an employee');
      return;
    }

    setLoading(true);
    try {
      const response = await getEmployeeAttendanceSummary(
        employeeSummary.selectedEmployeeId,
        employeeSummary.startDate,
        employeeSummary.endDate
      );
      if (response.data.success) {
        setEmployeeSummary(prev => ({
          ...prev,
          data: response.data.data
        }));
      }
    } catch (error) {
      showMessage('error', 'Failed to fetch employee attendance summary');
    } finally {
      setLoading(false);
    }
  };

  // Update Attendance
  const handleUpdateAttendance = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updateData = {
        status: editModal.attendance.status,
        notes: editModal.attendance.notes
      };
      const response = await updateAttendance(editModal.attendance._id, updateData);
      if (response.data.success) {
        showMessage('success', 'Attendance updated successfully');
        setEditModal({ isOpen: false, attendance: null });
        if (activeView === 'view') fetchAttendanceByDate();
        if (activeView === 'report') fetchReport();
      }
    } catch (error) {
      showMessage('error', 'Failed to update attendance');
    } finally {
      setLoading(false);
    }
  };

  // Delete Attendance
  const handleDeleteAttendance = async (attendanceId) => {
    if (!window.confirm('Are you sure you want to delete this attendance record?')) return;
    
    setLoading(true);
    try {
      const response = await deleteAttendance(attendanceId);
      if (response.data.success) {
        showMessage('success', 'Attendance deleted successfully');
        if (activeView === 'view') fetchAttendanceByDate();
        if (activeView === 'report') fetchReport();
      }
    } catch (error) {
      showMessage('error', 'Failed to delete attendance');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (attendance) => {
    setEditModal({
      isOpen: true,
      attendance: { ...attendance }
    });
  };

  return (
    <div className="p-3 sm:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-blue-100 rounded-lg p-2 sm:p-3 flex-shrink-0">
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-3xl font-bold text-gray-800">Attendance Management</h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Track and manage employee attendance records</p>
              </div>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <div className="flex-1 sm:flex-none sm:text-right">
                <label className="block text-xs text-gray-600 mb-1">Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white font-medium text-sm"
                >
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
              <div className="flex-1 sm:flex-none sm:text-right">
                <label className="block text-xs text-gray-600 mb-1">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white font-medium text-sm"
                >
                  <option value="2023">2023</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                  <option value="2027">2027</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-4 sm:mb-6 overflow-hidden">
          <div className="overflow-x-auto border-b border-gray-200">
            <div className="flex min-w-max sm:min-w-0">
            <button
              onClick={() => setActiveView('dashboard')}
              className={`px-4 sm:px-6 py-3 sm:py-4 font-medium transition-all flex items-center gap-2 whitespace-nowrap text-sm sm:text-base ${
                activeView === 'dashboard'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveView('mark')}
              className={`px-4 sm:px-6 py-3 font-medium whitespace-nowrap text-sm sm:text-base ${
                activeView === 'mark'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Mark Attendance
            </button>
            <button
              onClick={() => setActiveView('view')}
              className={`px-4 sm:px-6 py-3 font-medium whitespace-nowrap text-sm sm:text-base ${
                activeView === 'view'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              View Attendance
            </button>
            <button
              onClick={() => setActiveView('report')}
              className={`px-4 sm:px-6 py-3 font-medium whitespace-nowrap text-sm sm:text-base ${
                activeView === 'report'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Reports
            </button>
            <button
              onClick={() => setActiveView('employee')}
              className={`px-4 sm:px-6 py-3 font-medium whitespace-nowrap text-sm sm:text-base ${
                activeView === 'employee'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Employee Summary
            </button>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {/* Dashboard View */}
            {activeView === 'dashboard' && (
              <div>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 sm:p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm font-medium">Total Present</p>
                        <p className="text-3xl font-bold mt-2">{dashboardData.summary.present}</p>
                      </div>
                      <div className="bg-blue-400 bg-opacity-30 rounded-full p-3">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-4 sm:p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-red-100 text-sm font-medium">Total Absent</p>
                        <p className="text-3xl font-bold mt-2">{dashboardData.summary.absent}</p>
                      </div>
                      <div className="bg-red-400 bg-opacity-30 rounded-full p-3">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 sm:p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm font-medium">Total Leave</p>
                        <p className="text-3xl font-bold mt-2">{dashboardData.summary.leave}</p>
                      </div>
                      <div className="bg-green-400 bg-opacity-30 rounded-full p-3">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4 sm:p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm font-medium">Total Days</p>
                        <p className="text-3xl font-bold mt-2">{dashboardData.summary.total}</p>
                      </div>
                      <div className="bg-purple-400 bg-opacity-30 rounded-full p-3">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Bar Chart - Daily Attendance */}
                  <div className="bg-white p-6 rounded-lg border shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Daily Attendance Trends</h3>
                    {dashboardData.monthlyStats.length === 0 || dashboardData.monthlyStats.every(s => s.present === 0 && s.absent === 0 && s.leave === 0 && s.halfDay === 0) ? (
                      <div className="flex items-center justify-center h-64 text-gray-500">
                        <div className="text-center">
                          <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <p className="mt-2">No data available for this month</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-end justify-between h-64 mb-4 px-2 overflow-x-auto">
                          {dashboardData.monthlyStats.map((stat, idx) => {
                            const total = stat.present + stat.absent + stat.leave + stat.halfDay;
                            const maxHeight = 240;
                            const maxValue = Math.max(...dashboardData.monthlyStats.map(s => s.present + s.absent + s.leave + s.halfDay), 1);
                            
                            return (
                              <div key={idx} className="flex flex-col items-center gap-1 flex-1 mx-0.5 group min-w-[20px]">
                                <div className="relative w-full flex flex-col-reverse items-center">
                                  {total === 0 ? (
                                    <div className="w-full h-2 bg-gray-200 rounded"></div>
                                  ) : (
                                    <>
                                      {stat.present > 0 && (
                                        <div
                                          className="bg-blue-500 w-full transition-all duration-300 group-hover:bg-blue-600"
                                          style={{ height: `${(stat.present / maxValue) * maxHeight}px`, minHeight: stat.present > 0 ? '4px' : '0' }}
                                          title={`Present: ${stat.present}`}
                                        />
                                      )}
                                      {stat.absent > 0 && (
                                        <div
                                          className="bg-red-500 w-full transition-all duration-300 group-hover:bg-red-600"
                                          style={{ height: `${(stat.absent / maxValue) * maxHeight}px`, minHeight: stat.absent > 0 ? '4px' : '0' }}
                                          title={`Absent: ${stat.absent}`}
                                        />
                                      )}
                                      {stat.leave > 0 && (
                                        <div
                                          className="bg-green-500 w-full transition-all duration-300 group-hover:bg-green-600"
                                          style={{ height: `${(stat.leave / maxValue) * maxHeight}px`, minHeight: stat.leave > 0 ? '4px' : '0' }}
                                          title={`Leave: ${stat.leave}`}
                                        />
                                      )}
                                      {stat.halfDay > 0 && (
                                        <div
                                          className="bg-yellow-500 w-full transition-all duration-300 group-hover:bg-yellow-600"
                                          style={{ height: `${(stat.halfDay / maxValue) * maxHeight}px`, minHeight: stat.halfDay > 0 ? '4px' : '0' }}
                                          title={`Half Day: ${stat.halfDay}`}
                                        />
                                      )}
                                    </>
                                  )}
                                  {/* Tooltip on hover */}
                                  <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                    <div>Day: {stat.day}</div>
                                    <div>P: {stat.present}</div>
                                    <div>A: {stat.absent}</div>
                                    <div>L: {stat.leave}</div>
                                    {stat.halfDay > 0 && <div>HD: {stat.halfDay}</div>}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-600 mt-2 font-medium">{stat.day}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-center gap-6 text-sm flex-wrap">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                            <span className="text-gray-700">Present</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="text-gray-700">Absent</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="text-gray-700">Leave</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <span className="text-gray-700">Half Day</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Pie Chart */}
                  <div className="bg-white p-6 rounded-lg border shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">Attendance Distribution</h3>
                    {dashboardData.summary.total === 0 ? (
                      <div className="flex items-center justify-center h-64 text-gray-500">
                        <div className="text-center">
                          <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <p className="mt-2">No data available</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <div className="relative w-64 h-64">
                          <svg viewBox="0 0 100 100" className="transform -rotate-90">
                            {/* Present - Blue */}
                            <circle
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke="#3B82F6"
                              strokeWidth="20"
                              strokeDasharray={`${(dashboardData.summary.present / dashboardData.summary.total) * 251.2} 251.2`}
                            />
                            {/* Absent - Red */}
                            <circle
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke="#EF4444"
                              strokeWidth="20"
                              strokeDasharray={`${(dashboardData.summary.absent / dashboardData.summary.total) * 251.2} 251.2`}
                              strokeDashoffset={`-${(dashboardData.summary.present / dashboardData.summary.total) * 251.2}`}
                            />
                            {/* Leave - Green */}
                            <circle
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke="#10B981"
                              strokeWidth="20"
                              strokeDasharray={`${(dashboardData.summary.leave / dashboardData.summary.total) * 251.2} 251.2`}
                              strokeDashoffset={`-${((dashboardData.summary.present + dashboardData.summary.absent) / dashboardData.summary.total) * 251.2}`}
                            />
                            {/* Half Day - Yellow */}
                            {dashboardData.summary.halfDay > 0 && (
                              <circle
                                cx="50"
                                cy="50"
                                r="40"
                                fill="none"
                                stroke="#F59E0B"
                                strokeWidth="20"
                                strokeDasharray={`${(dashboardData.summary.halfDay / dashboardData.summary.total) * 251.2} 251.2`}
                                strokeDashoffset={`-${((dashboardData.summary.present + dashboardData.summary.absent + dashboardData.summary.leave) / dashboardData.summary.total) * 251.2}`}
                              />
                            )}
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-3xl font-bold text-gray-800">{dashboardData.summary.total}</div>
                              <div className="text-sm text-gray-600">Total Days</div>
                            </div>
                          </div>
                        </div>
                        <div className="ml-8 space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                            <div>
                              <div className="text-sm font-medium text-gray-700">Present</div>
                              <div className="text-lg font-bold text-gray-900">
                                {dashboardData.summary.present} ({((dashboardData.summary.present / dashboardData.summary.total) * 100).toFixed(1)}%)
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                            <div>
                              <div className="text-sm font-medium text-gray-700">Absent</div>
                              <div className="text-lg font-bold text-gray-900">
                                {dashboardData.summary.absent} ({((dashboardData.summary.absent / dashboardData.summary.total) * 100).toFixed(1)}%)
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                            <div>
                              <div className="text-sm font-medium text-gray-700">Leave</div>
                              <div className="text-lg font-bold text-gray-900">
                                {dashboardData.summary.leave} ({((dashboardData.summary.leave / dashboardData.summary.total) * 100).toFixed(1)}%)
                              </div>
                            </div>
                          </div>
                          {dashboardData.summary.halfDay > 0 && (
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                              <div>
                                <div className="text-sm font-medium text-gray-700">Half Day</div>
                                <div className="text-lg font-bold text-gray-900">
                                  {dashboardData.summary.halfDay} ({((dashboardData.summary.halfDay / dashboardData.summary.total) * 100).toFixed(1)}%)
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Attendance Details Table */}
                <div className="bg-white rounded-lg border overflow-hidden shadow-md">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 font-semibold flex items-center justify-between">
                    <span>Attendance Details - {new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                    <span className="text-sm font-normal">Total Employees: {dashboardData.attendanceDetails.length}</span>
                  </div>
                  
                  {loading ? (
                    <div className="text-center py-12">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-gray-600">Loading attendance data...</p>
                    </div>
                  ) : dashboardData.attendanceDetails.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="mt-2">No attendance records found for {new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 border-b-2 border-gray-300">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700 sticky left-0 bg-gray-100 z-10">Employee</th>
                            <th className="px-4 py-3 text-center font-semibold text-gray-700">Department</th>
                            <th className="px-4 py-3 text-center font-semibold text-green-700">Present</th>
                            <th className="px-4 py-3 text-center font-semibold text-red-700">Absent</th>
                            <th className="px-4 py-3 text-center font-semibold text-yellow-700">Half Day</th>
                            <th className="px-4 py-3 text-center font-semibold text-blue-700">Leave</th>
                            <th className="px-4 py-3 text-center font-semibold text-gray-700">Total Days</th>
                            <th className="px-4 py-3 text-center font-semibold text-gray-700">Attendance %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.attendanceDetails.map((empData, idx) => {
                            const summary = empData.summary || {};
                            const present = summary.present || 0;
                            const absent = summary.absent || 0;
                            const halfDay = summary['half-day'] || 0;
                            const leave = summary.leave || 0;
                            const totalDays = present + absent + halfDay + leave;
                            const attendancePercentage = totalDays > 0 ? ((present + halfDay * 0.5) / totalDays * 100).toFixed(2) : '0.00';
                            
                            return (
                              <tr key={idx} className="border-b hover:bg-blue-50 transition-colors">
                                <td className="px-4 py-3 font-medium text-gray-900 sticky left-0 bg-white hover:bg-blue-50">
                                  <div>
                                    <div>{empData.employee.firstName} {empData.employee.lastName}</div>
                                    <div className="text-xs text-gray-500">{empData.employeeId}</div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center text-gray-700">{empData.employee.department || 'N/A'}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-flex items-center justify-center w-12 h-8 bg-green-100 text-green-800 rounded-md font-semibold">
                                    {present}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-flex items-center justify-center w-12 h-8 bg-red-100 text-red-800 rounded-md font-semibold">
                                    {absent}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-flex items-center justify-center w-12 h-8 bg-yellow-100 text-yellow-800 rounded-md font-semibold">
                                    {halfDay}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-flex items-center justify-center w-12 h-8 bg-blue-100 text-blue-800 rounded-md font-semibold">
                                    {leave}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center font-semibold text-gray-900">{totalDays}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full font-semibold ${
                                    parseFloat(attendancePercentage) >= 90 ? 'bg-green-100 text-green-800' :
                                    parseFloat(attendancePercentage) >= 75 ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {attendancePercentage}%
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mark Attendance Tab */}
            {activeView === 'mark' && (
              <form onSubmit={handleMarkAttendance} className="max-w-2xl">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Employee
                    </label>
                    <select
                      value={markForm.employeeId}
                      onChange={(e) => setMarkForm({ ...markForm, employeeId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">-- Select Employee --</option>
                      {employees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.firstName} {emp.lastName} ({emp.employeeId}) - {emp.department || 'N/A'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      value={markForm.date}
                      onChange={(e) => setMarkForm({ ...markForm, date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={markForm.status}
                      onChange={(e) => setMarkForm({ ...markForm, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                      <option value="half-day">Half Day</option>
                      <option value="leave">Leave</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={markForm.notes}
                      onChange={(e) => setMarkForm({ ...markForm, notes: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="3"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                  >
                    {loading ? 'Marking...' : 'Mark Attendance'}
                  </button>
                </div>
              </form>
            )}

            {/* View Attendance Tab */}
            {activeView === 'view' && (
              <div>
                <div className="mb-6 flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700">Select Date:</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={fetchAttendanceByDate}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Refresh
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : attendanceList.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No attendance records found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Employee</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Check In</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Check Out</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Work Hours</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Notes</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceList.map((record) => (
                          <tr key={record._id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              {record.employee?.firstName} {record.employee?.lastName}
                              <br />
                              <span className="text-gray-500 text-xs">{record.employee?.employeeId}</span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {new Date(record.date).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[record.status]}`}>
                                {record.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {record.checkIn ? new Date(record.checkIn).toLocaleTimeString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {record.checkOut ? new Date(record.checkOut).toLocaleTimeString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm">{record.workHours || 0}h</td>
                            <td className="px-4 py-3 text-sm">{record.notes || '-'}</td>
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={() => openEditModal(record)}
                                className="text-blue-600 hover:text-blue-800 mr-3"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteAttendance(record._id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Report Tab */}
            {activeView === 'report' && (
              <div>
                <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={reportFilters.startDate}
                      onChange={(e) => setReportFilters({ ...reportFilters, startDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={reportFilters.endDate}
                      onChange={(e) => setReportFilters({ ...reportFilters, endDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                    <input
                      type="text"
                      value={reportFilters.department}
                      onChange={(e) => setReportFilters({ ...reportFilters, department: e.target.value })}
                      placeholder="Optional"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={fetchReport}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      Generate Report
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : reportData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No data available</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Employee</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Department</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Present</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Absent</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Half Day</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Leave</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Total Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((item) => (
                          <tr key={item.employee._id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              {item.employee.firstName} {item.employee.lastName}
                              <br />
                              <span className="text-gray-500 text-xs">{item.employee.employeeId}</span>
                            </td>
                            <td className="px-4 py-3 text-sm">{item.employee.department}</td>
                            <td className="px-4 py-3 text-center text-sm">{item.summary.present || 0}</td>
                            <td className="px-4 py-3 text-center text-sm">{item.summary.absent || 0}</td>
                            <td className="px-4 py-3 text-center text-sm">{item.summary['half-day'] || 0}</td>
                            <td className="px-4 py-3 text-center text-sm">{item.summary.leave || 0}</td>
                            <td className="px-4 py-3 text-center text-sm font-semibold">{item.summary.totalDays || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Employee Summary Tab */}
            {activeView === 'employee' && (
              <div>
                {/* Filters */}
                <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Employee</label>
                    <select
                      value={employeeSummary.selectedEmployeeId}
                      onChange={(e) => setEmployeeSummary(prev => ({ ...prev, selectedEmployeeId: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Select Employee --</option>
                      {employees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.firstName} {emp.lastName} ({emp.employeeId}) - {emp.department || 'N/A'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={employeeSummary.startDate}
                      onChange={(e) => setEmployeeSummary(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={employeeSummary.endDate}
                      onChange={(e) => setEmployeeSummary(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={fetchEmployeeSummary}
                      disabled={!employeeSummary.selectedEmployeeId}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Get Summary
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Loading employee summary...</p>
                  </div>
                ) : !employeeSummary.data ? (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <p className="mt-2">Select an employee and date range to view attendance summary</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Employee Info Card */}
                    <div className="bg-white rounded-lg border shadow-md p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-100 rounded-full p-4">
                          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-800">
                            {employeeSummary.data.employee.firstName} {employeeSummary.data.employee.lastName}
                          </h2>
                          <p className="text-gray-600">
                            {employeeSummary.data.employee.employeeId} • {employeeSummary.data.employee.department} • {employeeSummary.data.employee.position}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            Period: {new Date(employeeSummary.startDate).toLocaleDateString()} - {new Date(employeeSummary.endDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-green-100 text-sm font-medium">Present Days</p>
                            <p className="text-3xl font-bold mt-2">{employeeSummary.data.summary.present || 0}</p>
                          </div>
                          <div className="bg-green-400 bg-opacity-30 rounded-full p-3">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-red-100 text-sm font-medium">Absent Days</p>
                            <p className="text-3xl font-bold mt-2">{employeeSummary.data.summary.absent || 0}</p>
                          </div>
                          <div className="bg-red-400 bg-opacity-30 rounded-full p-3">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-blue-100 text-sm font-medium">Leave Days</p>
                            <p className="text-3xl font-bold mt-2">{employeeSummary.data.summary.leave || 0}</p>
                          </div>
                          <div className="bg-blue-400 bg-opacity-30 rounded-full p-3">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-yellow-100 text-sm font-medium">Half Days</p>
                            <p className="text-3xl font-bold mt-2">{employeeSummary.data.summary['half-day'] || 0}</p>
                          </div>
                          <div className="bg-yellow-400 bg-opacity-30 rounded-full p-3">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Attendance Percentage */}
                    <div className="bg-white rounded-lg border shadow-md p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Attendance Statistics</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-green-600">
                            {(() => {
                              const total = (employeeSummary.data.summary.present || 0) + 
                                          (employeeSummary.data.summary.absent || 0) + 
                                          (employeeSummary.data.summary.leave || 0) + 
                                          (employeeSummary.data.summary['half-day'] || 0);
                              const present = employeeSummary.data.summary.present || 0;
                              const halfDay = employeeSummary.data.summary['half-day'] || 0;
                              return total > 0 ? ((present + halfDay * 0.5) / total * 100).toFixed(1) : '0.0';
                            })()}%
                          </div>
                          <p className="text-gray-600 mt-1">Attendance Rate</p>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-blue-600">
                            {(() => {
                              const total = (employeeSummary.data.summary.present || 0) + 
                                          (employeeSummary.data.summary.absent || 0) + 
                                          (employeeSummary.data.summary.leave || 0) + 
                                          (employeeSummary.data.summary['half-day'] || 0);
                              return total;
                            })()}
                          </div>
                          <p className="text-gray-600 mt-1">Total Working Days</p>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-purple-600">
                            {(() => {
                              const present = employeeSummary.data.summary.present || 0;
                              const halfDay = employeeSummary.data.summary['half-day'] || 0;
                              return (present + halfDay * 0.5).toFixed(1);
                            })()}
                          </div>
                          <p className="text-gray-600 mt-1">Effective Present Days</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Edit Attendance</h2>
            <form onSubmit={handleUpdateAttendance}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={editModal.attendance.status}
                    onChange={(e) => setEditModal({
                      ...editModal,
                      attendance: { ...editModal.attendance, status: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="half-day">Half Day</option>
                    <option value="leave">Leave</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={editModal.attendance.notes || ''}
                    onChange={(e) => setEditModal({
                      ...editModal,
                      attendance: { ...editModal.attendance, notes: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {loading ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditModal({ isOpen: false, attendance: null })}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceManagement;
